import * as React from 'react';
import Drawer from 'antd/lib/drawer';
import Collapse from 'antd/lib/collapse';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UnorderedListOutlined } from '@ant-design/icons';
import { IssueEntity } from '@/types';
import { RootState } from '@/reducers/reducers';
import { antGrey, titleStyle } from '@/components/styles/globalStyles';
import {
    selectAppIssueById,
    selectAppPlatform,
    selectAppStoreType,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectIssueTimeline,
    selectUserCanPerformAction,
} from '@/selectors/selectors';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { formatDateWithTimezone } from '@/timeHelpers';
import {
    TRY_FETCH_ISSUE,
    TRY_FETCH_ISSUE_TIMELINE,
    TRY_UNMERGE_ISSUE_SOURCES,
    TriageViewType,
    tryFetchIssue,
    tryFetchIssueTimeline,
    tryUnmergeIssueSources,
} from '@/actions/triage';
import { useIsLoading } from '@/utils/hooks/helpers';
import { FixRequestSection } from './FixRequestSection';
import { IssueSection } from './IssueSection';
import { MetadataSourceSection } from './MetadataSourceSection';
import { PotentialCausesSection } from './PotentialCausesSection';
import { WorkSection } from './WorkSection';
import { TimelineEventItem } from '@/components/timelineEvents/TimelineEventItem';
import { ReleaseTimelineSectionContext } from '@/components/timelineEvents/EventsTimeline';

interface TriageIssueDrawerProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
    basePath: string;
}

export const TriageIssueDrawer: React.FC<TriageIssueDrawerProps> = ({
    open,
    onClose,
    orgId,
    issue,
    viewType,
    basePath,
}) => {
    const dispatch = useDispatch();
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, issue.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, issue.appId));
    const timeline = useSelector((state: RootState) => selectIssueTimeline(state, issue.id));
    const isLoadingTimeline = useIsLoading(TRY_FETCH_ISSUE_TIMELINE, issue.id);
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );
    const isUnmerging = useIsLoading(TRY_UNMERGE_ISSUE_SOURCES, issue.id);
    const [unmergingSourceId, setUnmergingSourceId] = React.useState<string | null>(null);
    const unmergeSource = (sourceId: string) => {
        setUnmergingSourceId(sourceId);
        dispatch(
            tryUnmergeIssueSources({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                sourceIds: [sourceId],
                mode: 'single',
            }),
        );
    };
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, issue.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, issue.appId));
    const timelineToday = dayjs().tz(effectiveTimezone);
    const timelineLastWeekStart = timelineToday.subtract(7, 'days');
    let timelineLastDate = dayjs.unix(0).tz(effectiveTimezone);
    const createdLabel = formatDateWithTimezone(
        issue.createdAt,
        'longMonthDateYearTime',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    const updatedLabel = formatDateWithTimezone(
        issue.updatedAt,
        'longMonthDateYearTime',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    React.useEffect(() => {
        if (open) {
            dispatch(tryFetchIssueTimeline({ orgId, appId: issue.appId, issueId: issue.id }));
        }
    }, [open, issue.id]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={680}
            closable={false}
            styles={{ header: { display: 'none' }, body: { padding: 0 } }}
        >
            <PotentialCausesSection potentialCauses={issue.potentialCauses} />
            <FixRequestSection orgId={orgId} issue={issue} viewType={viewType} />
            <IssueSection orgId={orgId} issue={issue} viewType={viewType} basePath={basePath} />
            <WorkSection issue={issue} orgId={orgId} />
            {(issue.sources ?? []).map((source) => (
                <MetadataSourceSection
                    key={source.id}
                    source={source}
                    createdLabel={createdLabel}
                    updatedLabel={updatedLabel}
                    appStoreType={appStoreType}
                    onUnmerge={(issue.sources?.length ?? 0) > 1 ? () => unmergeSource(source.id) : undefined}
                    unmergeDisabled={!canManage}
                    unmergeBusy={isUnmerging}
                    unmergeLoading={isUnmerging && unmergingSourceId === source.id}
                />
            ))}
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        style: { borderTop: `1px solid ${antGrey.grey4}` },
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <UnorderedListOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Timeline</Text>
                            </div>
                        ),
                        children: (
                            <div style={{ padding: 16, paddingTop: 0, paddingBottom: 80 }}>
                                {isLoadingTimeline && timeline.length === 0 ?
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                                        <Spin />
                                    </div>
                                : timeline.length === 0 ?
                                    <Text style={{ color: antGrey.grey7 }}>No history yet.</Text>
                                :   timeline.map((event) => {
                                        const triggeredAtDate = dayjs(event.triggeredAt).tz(effectiveTimezone);
                                        let showDivider = false;
                                        let dividerLabel = formatDateWithTimezone(
                                            event.triggeredAt,
                                            'shortMonthDate',
                                            effectiveTimezone,
                                            use24HourTimeFormat,
                                        );
                                        if (!timelineLastDate.isSame(triggeredAtDate, 'day')) {
                                            showDivider = true;
                                            if (
                                                triggeredAtDate.isAfter(timelineLastWeekStart) &&
                                                timelineToday.isAfter(triggeredAtDate)
                                            ) {
                                                dividerLabel = formatDateWithTimezone(
                                                    event.triggeredAt,
                                                    'dayMonthDateYear',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                );
                                            }
                                        }
                                        timelineLastDate = triggeredAtDate;
                                        return (
                                            <React.Fragment key={event.triggeredAt + event.type}>
                                                {showDivider && (
                                                    <Divider dashed style={{ borderColor: antGrey.grey5 }}>
                                                        <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 12 }}>
                                                            {dividerLabel}
                                                        </Text>
                                                    </Divider>
                                                )}
                                                <TimelineEventItem
                                                    timelineEvent={event}
                                                    orgId={orgId}
                                                    appId={issue.appId}
                                                    appPlatform={appPlatform}
                                                    appStoreType={appStoreType}
                                                    context={ReleaseTimelineSectionContext.AppTimeline}
                                                />
                                            </React.Fragment>
                                        );
                                    })
                                }
                            </div>
                        ),
                    },
                ]}
            />
        </Drawer>
    );
};

export const RoutedTriageIssueDrawer: React.FC<{
    orgId: string;
    appId: string;
    basePath: string;
    issueId?: string;
    viewType: TriageViewType;
}> = ({ orgId, appId, basePath, issueId, viewType }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const issue = useSelector((state: RootState) => (issueId ? selectAppIssueById(state, appId, issueId) : undefined));
    const isLoading = useIsLoading(TRY_FETCH_ISSUE, issueId ?? '');
    // Always fetch the full issue when the drawer opens: the list carries a lighter copy without work items.
    // attemptedRef stops a failed fetch from looping; reset on close so re-opening re-fetches.
    const attemptedRef = React.useRef<string | undefined>(undefined);

    React.useEffect(() => {
        if (!issueId) {
            attemptedRef.current = undefined;
            return;
        }
        if (attemptedRef.current !== issueId) {
            attemptedRef.current = issueId;
            dispatch(tryFetchIssue({ orgId, appId, issueId }));
        }
    }, [issueId, orgId, appId, dispatch]);

    const close = () => history.push(basePath);

    if (!issueId) {
        return null;
    }
    if (issue) {
        return (
            <TriageIssueDrawer
                open
                onClose={close}
                orgId={orgId}
                issue={issue}
                viewType={viewType}
                basePath={basePath}
            />
        );
    }
    return (
        <Drawer open onClose={close} placement="right" width={680} styles={{ body: { padding: 0 } }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                {isLoading ?
                    <Spin />
                :   <Empty description="Issue not found" />}
            </div>
        </Drawer>
    );
};
